import { apiClient, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * P5 — the analytics client (`/v1/analytics/*`, OPSFL-CLIENT-PLATFORM FSD §8). Read-only, all
 * `analytics.read`, bounded by construction (from/to REQUIRED, capped window). Every response
 * carries the `sources` map convention: a metric with no real source is NULL with a stated
 * reason (keys prefixed `_`), never a plausible zero — the UI renders "—" plus the reason,
 * it NEVER coerces null to 0 (the oversight.ts rule).
 */

export type SourcesMap = Record<string, string | null>;

export interface AnalyticsWindow {
  from: string;
  to: string;
}

export interface CaseTotals {
  opened: number | null;
  closed: number | null;
  open: number | null;
  slaBreached: number | null;
}

export interface RunTotals {
  started: number | null;
  completed: number | null;
  failed: number | null;
}

export interface AlertTotals {
  open: number | null;
  high: number | null;
  routed: number | null;
}

export interface AnalyticsSummary {
  hub: string;
  window: AnalyticsWindow;
  cases: CaseTotals | null;
  runs: RunTotals | null;
  alerts: AlertTotals | null;
  repeatSignalRate: number | null;
  sources: SourcesMap;
}

export interface CaseBucket {
  bucketStart: string;
  opened: number;
  closed: number;
}

export interface AnalyticsCases {
  hub: string;
  window: AnalyticsWindow;
  bucket: 'day' | 'week';
  buckets: CaseBucket[];
  sources: SourcesMap;
}

export interface AgeingBucket {
  label: string;
  count: number;
}

export interface AnalyticsSla {
  hub: string;
  window: AnalyticsWindow;
  withSla: number | null;
  breached: number | null;
  breachRate: number | null;
  openAgeing: AgeingBucket[];
  sources: SourcesMap;
}

export interface DispositionSlice {
  action: string;
  count: number;
}

export interface AnalyticsDispositions {
  hub: string;
  window: AnalyticsWindow;
  mix: DispositionSlice[];
  sources: SourcesMap;
}

export interface MeterCounts {
  total: number;
  bySpoke: Record<string, number>;
}

export interface AnalyticsUsage {
  hub: string;
  window: AnalyticsWindow;
  meter: {
    hub: string;
    period: { from: string; to: string };
    entitiesUnderManagement: MeterCounts;
    agentCaseCount: MeterCounts;
    meterKey: string;
    sources: SourcesMap;
  } | null;
  sources: SourcesMap;
}

export interface AnalyticsQuery {
  from: string;
  to: string;
}

const get = async <T>(path: string, params: object): Promise<T> => {
  const { data } = await apiClient.get<ApiEnvelope<T>>(path, { params });
  return unwrap(data);
};

export const getAnalyticsSummary = (q: AnalyticsQuery) =>
  get<AnalyticsSummary>('/v1/analytics/summary', q);

export const getAnalyticsCases = (q: AnalyticsQuery & { bucket: 'day' | 'week' }) =>
  get<AnalyticsCases>('/v1/analytics/cases', q);

export const getAnalyticsSla = (q: AnalyticsQuery) => get<AnalyticsSla>('/v1/analytics/sla', q);

export const getAnalyticsDispositions = (q: AnalyticsQuery) =>
  get<AnalyticsDispositions>('/v1/analytics/dispositions', q);

export const getAnalyticsUsage = (q: AnalyticsQuery) =>
  get<AnalyticsUsage>('/v1/analytics/usage', q);

// ---- OL-28: analytics and tracking parity (PROPOSED: PROPOSAL-ol-28-analytics-parity.md) ----------

export type TrendSeries =
  | 'runs.outcome'
  | 'runs.flow'
  | 'cases.stage'
  | 'cases.type'
  | 'cases.sla'
  | 'cases.outcome';

export type TrendBucketSize = 'day' | 'week' | 'month';

export interface TrendBucket {
  bucketStart: string;
  counts: Record<string, number>;
}

/** `groups`/`buckets`/`totals` are null (with `sources._<series>`) when the series has no source for you. */
export interface AnalyticsTrend {
  hub: string;
  window: AnalyticsWindow;
  series: TrendSeries;
  bucket: TrendBucketSize;
  groups: string[] | null;
  buckets: TrendBucket[] | null;
  totals: Record<string, number> | null;
  sources: SourcesMap;
}

export const getAnalyticsTrend = (
  q: AnalyticsQuery & { series: TrendSeries; bucket: TrendBucketSize },
) => get<AnalyticsTrend>('/v1/analytics/trends', q);

export type StatusClass = '2xx' | '3xx' | '4xx' | '429' | '5xx';

export interface CredentialUsage {
  credentialType: 'API_KEY' | 'API_CLIENT';
  credentialId: string;
  name: string | null;
  status: 'ACTIVE' | 'REVOKED' | 'DISABLED' | 'UNKNOWN';
  total: number;
  byStatusClass: Record<StatusClass, number>;
  byRoute: { routeFamily: string; count: number }[];
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface ApiUsageReport {
  hub: string;
  window: AnalyticsWindow;
  bucket: 'hour' | 'day';
  total: number;
  credentialCount: number;
  credentials: CredentialUsage[];
  series: { bucketStart: string; total: number; byStatusClass: Record<StatusClass, number> }[];
  sources: SourcesMap;
}

export const getApiUsage = (q: AnalyticsQuery & { bucket: 'hour' | 'day'; credentialId?: string }) =>
  get<ApiUsageReport>('/v1/analytics/api-usage', q);

export type WidgetSize = 'S' | 'M' | 'L';

export interface WidgetSpec {
  id: string;
  title: string;
  description: string;
  category: string;
  requiredKey: string;
  sizes: WidgetSize[];
  defaultSize: WidgetSize;
}

export interface DashboardWidget {
  widget: string;
  size: WidgetSize;
}

export interface AnalyticsDashboard {
  hub: string;
  layout: DashboardWidget[];
  version: number;
  isDefault: boolean;
  updatedAt: string | null;
  maxWidgets: number;
  catalogue: WidgetSpec[];
}

export const getAnalyticsDashboard = () => get<AnalyticsDashboard>('/v1/analytics/dashboard', {});

export async function saveAnalyticsDashboard(
  layout: DashboardWidget[],
  version: number,
): Promise<AnalyticsDashboard> {
  const { data } = await apiClient.put<ApiEnvelope<AnalyticsDashboard>>('/v1/analytics/dashboard', {
    layout,
    version,
  });
  return unwrap(data);
}

export async function resetAnalyticsDashboard(): Promise<AnalyticsDashboard> {
  const { data } = await apiClient.post<ApiEnvelope<AnalyticsDashboard>>(
    '/v1/analytics/dashboard/reset',
  );
  return unwrap(data);
}

export const AUDIT_SOURCES = [
  'case_action',
  'work_item',
  'triage',
  'flow_approval',
  'document_open',
] as const;
export type AuditSource = (typeof AUDIT_SOURCES)[number];

/**
 * Fetches with the bearer, then hands the browser a file (a bare link would carry no token). A refused
 * download's body arrives as a Blob: it is read back into JSON so the caller sees the real error code.
 */
async function saveFile(path: string, params: object, filename: string): Promise<void> {
  let blob: Blob;
  try {
    const { data } = await apiClient.get<Blob>(path, { params, responseType: 'blob' });
    blob = data;
  } catch (e) {
    const err = e as { response?: { data?: unknown } };
    const body = err.response?.data;
    if (body instanceof Blob) {
      try {
        err.response!.data = JSON.parse(await body.text());
      } catch {
        // not JSON: leave it, normalizeApiError falls back to the status
      }
    }
    throw e;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const downloadAuditExport = (
  q: AnalyticsQuery & { format: 'csv' | 'json'; sources: AuditSource[] },
) =>
  saveFile(
    '/v1/analytics/audit-export',
    { from: q.from, to: q.to, format: q.format, sources: q.sources.join(',') },
    `audit-trail-${q.from.slice(0, 10)}-${q.to.slice(0, 10)}.${q.format}`,
  );

export const downloadApiUsageCsv = (q: AnalyticsQuery) =>
  saveFile('/v1/analytics/api-usage/export', q, `api-usage-${q.from.slice(0, 10)}-${q.to.slice(0, 10)}.csv`);
